/**
 * Dock Console — type an action name (or a command label) and run it.
 *
 * This is not a Python/JS REPL. It is the workstation command line: the same
 * catalog the palette searches, for people who already know the action id.
 */

import * as React from "react";

import { EmptyState } from "@/components/ui/primitives";
import { dispatchAction } from "@/shell/actions";
import { listCommands, searchCommands } from "@/shell/commands";
import { useLogLines } from "@/store/appStore";
import { cn } from "@/lib/utils";

export function ConsolePanel() {
  const lines = useLogLines();
  const [input, setInput] = React.useState("");
  const [echo, setEcho] = React.useState<{ ts: string; text: string; ok: boolean }[]>([]);
  const endRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    endRef.current?.scrollIntoView({ block: "end" });
  }, [echo, lines]);

  const run = (raw: string) => {
    const text = raw.trim();
    if (!text) return;
    const ts = new Date().toISOString().slice(11, 19);
    if (text === "help") {
      setEcho((prev) => [
        ...prev,
        { ts, text: "Type an action id (home.openCase) or a command name. help / commands lists the catalog.", ok: true },
      ]);
      return;
    }
    if (text === "commands") {
      const names = listCommands()
        .slice(0, 30)
        .map((c) => c.action)
        .join(", ");
      setEcho((prev) => [...prev, { ts, text: names, ok: true }]);
      return;
    }
    const match =
      listCommands().find((c) => c.action === text || c.id === text) ?? searchCommands(text)[0];
    if (!match) {
      setEcho((prev) => [...prev, { ts, text: `Unknown command: ${text}`, ok: false }]);
      return;
    }
    dispatchAction(match.action);
    setEcho((prev) => [...prev, { ts, text: `→ ${match.path}`, ok: true }]);
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="min-h-0 flex-1 overflow-auto px-3 py-1.5 font-mono text-[11px] leading-[1.45]">
        {echo.length === 0 && lines.length === 0 ? (
          <EmptyState
            title="Console"
            hint="Type an action id and press Enter. Pipeline log lines also appear here."
          />
        ) : (
          <>
            {lines.slice(-80).map((line) => (
              <div key={line.seq} className="flex gap-2 whitespace-pre-wrap text-ads-muted">
                <span className="shrink-0 text-ads-faint">{line.ts.slice(11, 19)}</span>
                <span>{line.line}</span>
              </div>
            ))}
            {echo.map((row, i) => (
              <div key={`${row.ts}-${i}`} className={cn("flex gap-2", row.ok ? "text-ads-text" : "text-red-600")}>
                <span className="shrink-0 text-ads-faint">{row.ts}</span>
                <span>{row.text}</span>
              </div>
            ))}
            <div ref={endRef} />
          </>
        )}
      </div>
      <form
        className="flex h-7 shrink-0 items-center border-t border-ads-border"
        onSubmit={(event) => {
          event.preventDefault();
          run(input);
          setInput("");
        }}
      >
        <span className="px-2 font-mono text-[11px] text-ads-muted">›</span>
        <input
          value={input}
          onChange={(event) => setInput(event.target.value)}
          placeholder="action id or command name"
          className="h-full min-w-0 flex-1 bg-transparent pr-2 font-mono text-[11.5px] outline-none"
        />
      </form>
    </div>
  );
}
